import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import UserNavbar from '../components/UserNavbar';
import FilterSelect from '../components/FilterSelect';
import DateInput from '../components/DateInput';

const TYPE_FILTER_OPTIONS = [
  { value: 'ALL', label: 'All' },
  { value: 'PAYMENT', label: 'Payment' },
  { value: 'REFUND', label: 'Refund' },
];

const PAGE_SIZE = 5;

const TABS = [
  { key: 'ALL', label: 'All Payments' },
  { key: 'SUCCEEDED', label: 'Successful' },
  { key: 'REFUNDED', label: 'Refunded' },
  { key: 'FAILED', label: 'Failed' },
];

function getMediaUrl(path) {
  if (!path) return null;
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
  if (typeof path === 'string' && (path.startsWith('http://') || path.startsWith('https://'))) return path;
  if (typeof path === 'string' && path.startsWith('/media/')) return `${apiUrl}${path}`;
  return `${apiUrl}/media/${path}`;
}

function doctorDisplayName(name) {
  if (!name) return 'Doctor';
  return name.startsWith('Dr') ? name : `Dr. ${name}`;
}

function doctorAvatar(name, image) {
  const url = getMediaUrl(image);
  if (url) return url;
  return `https://ui-avatars.com/api/?name=${encodeURIComponent(doctorDisplayName(name))}&background=DBEAFE&color=1D4ED8`;
}

function formatMoney(amount) {
  const n = Number(amount);
  if (Number.isNaN(n)) return '—';
  return `$${n.toFixed(2)}`;
}

function formatDatePart(iso) {
  if (!iso) return { date: '—', time: '' };
  try {
    const d = new Date(iso);
    return {
      date: d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }),
      time: d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }),
    };
  } catch {
    return { date: iso, time: '' };
  }
}

function toInputDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatRangeLabel(from, to) {
  const fmt = (s) => {
    if (!s) return '';
    const d = new Date(`${s}T00:00:00`);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };
  if (!from && !to) return 'All time';
  if (from && to) return `${fmt(from)} - ${fmt(to)}`;
  if (from) return `From ${fmt(from)}`;
  return `Until ${fmt(to)}`;
}

function callTypeLabel(callType) {
  if (!callType) return 'Consultation';
  return callType === 'AUDIO' ? 'Audio Consultation' : 'Video Consultation';
}

function UserPaymentHistory() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [payments, setPayments] = useState([]);
  const [tab, setTab] = useState('ALL');
  const [typeFilter, setTypeFilter] = useState('ALL');
  const [page, setPage] = useState(1);
  const [openMenuId, setOpenMenuId] = useState(null);

  const today = useMemo(() => new Date(), []);
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return toInputDate(d);
  });
  const [dateTo, setDateTo] = useState(() => toInputDate(today));
  const [showDatePicker, setShowDatePicker] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get('payments/history/');
        setPayments(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error(err);
        if (err.response?.status === 401) navigate('/user/login');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [navigate]);

  useEffect(() => {
    const close = () => {
      setOpenMenuId(null);
      setShowDatePicker(false);
    };
    if (openMenuId != null || showDatePicker) {
      window.addEventListener('click', close);
      return () => window.removeEventListener('click', close);
    }
    return undefined;
  }, [openMenuId, showDatePicker]);

  const inDateRange = (p) => {
    if (!p.created_at) return true;
    const t = new Date(p.created_at).getTime();
    if (dateFrom) {
      const start = new Date(`${dateFrom}T00:00:00`).getTime();
      if (t < start) return false;
    }
    if (dateTo) {
      const end = new Date(`${dateTo}T23:59:59`).getTime();
      if (t > end) return false;
    }
    return true;
  };

  const rangePayments = useMemo(
    () => payments.filter(inDateRange),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [payments, dateFrom, dateTo]
  );

  const totalPaid = rangePayments
    .filter((p) => p.status === 'SUCCEEDED')
    .reduce((sum, p) => sum + (Number(p.total_amount) || 0), 0);

  const totalRefunded = rangePayments
    .filter((p) => p.status === 'REFUNDED')
    .reduce((sum, p) => sum + (Number(p.total_amount) || 0), 0);

  const totalSpent = Math.max(0, totalPaid - totalRefunded);

  const filtered = useMemo(() => {
    return rangePayments.filter((p) => {
      if (tab === 'SUCCEEDED' && p.status !== 'SUCCEEDED') return false;
      if (tab === 'REFUNDED' && p.status !== 'REFUNDED') return false;
      if (tab === 'FAILED' && p.status !== 'FAILED') return false;

      if (typeFilter === 'PAYMENT' && p.status === 'REFUNDED') return false;
      if (typeFilter === 'REFUND' && p.status !== 'REFUNDED') return false;
      return true;
    });
  }, [rangePayments, tab, typeFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const showingFrom = filtered.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const showingTo = Math.min(currentPage * PAGE_SIZE, filtered.length);

  useEffect(() => {
    setPage(1);
  }, [tab, typeFilter, dateFrom, dateTo]);

  const goToReceipt = (p) => {
    if (!p.appointment_id) return;
    navigate(
      `/user/booking-summary?appointment_id=${p.appointment_id}&payment_id=${p.payment_id}`,
      {
        state: {
          fromPaymentHistory: true,
          payment: p,
          backTo: '/user/payment-history',
        },
      }
    );
  };

  const pageNumbers = useMemo(() => {
    if (totalPages <= 5) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const nums = [1];
    if (currentPage > 3) nums.push('…');
    for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i += 1) {
      if (!nums.includes(i)) nums.push(i);
    }
    if (currentPage < totalPages - 2) nums.push('…');
    if (!nums.includes(totalPages)) nums.push(totalPages);
    return nums;
  }, [currentPage, totalPages]);

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#F3F6FB' }}>
        <UserNavbar />
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
          <div className="spinner-border text-primary" role="status" />
        </div>
      </div>
    );
  }

  const stats = [
    {
      label: 'Total Payments',
      value: String(rangePayments.length),
      footer: 'All Time',
      icon: 'bi-wallet2',
      iconBg: '#DBEAFE',
      iconColor: '#2563EB',
    },
    {
      label: 'Successful Paid',
      value: formatMoney(totalPaid),
      footer: 'All Time',
      icon: 'bi-check-circle',
      iconBg: '#D1FAE5',
      iconColor: '#059669',
    },
    {
      label: 'Refunded',
      value: formatMoney(totalRefunded),
      footer: 'All Time',
      icon: 'bi-arrow-counterclockwise',
      iconBg: '#DBEAFE',
      iconColor: '#2563EB',
    },
    {
      label: 'Total Spent',
      value: formatMoney(totalSpent),
      footer: 'All Time',
      icon: 'bi-file-earmark-text',
      iconBg: '#EDE9FE',
      iconColor: '#7C3AED',
    },
  ];

  return (
    <div style={{ minHeight: '100vh', background: '#F3F6FB' }}>
      <UserNavbar />

      <div className="container-fluid px-3 px-md-4 py-3 py-md-4" style={{ maxWidth: 1200 }}>
        {/* Header */}
        <div className="d-flex flex-wrap align-items-start justify-content-between gap-3 mb-3 mb-md-4">
          <div className="d-flex align-items-start gap-2">
            <button
              type="button"
              className="btn btn-link text-decoration-none text-dark p-0 mt-1"
              onClick={() => navigate('/user/settings')}
              aria-label="Back"
            >
              <i className="bi bi-arrow-left fs-5" />
            </button>
            <div>
              <h2 className="fw-bold mb-0 text-dark" style={{ fontSize: 'clamp(1.35rem, 3vw, 1.75rem)' }}>
                Payment History
              </h2>
              <p className="text-muted mb-0" style={{ fontSize: 13 }}>
                Your booking payments and receipts
              </p>
            </div>
          </div>

          <div className="position-relative">
            <button
              type="button"
              className="btn bg-white border d-flex align-items-center gap-2 rounded-3 px-3 py-2"
              style={{ borderColor: '#E5E7EB', fontSize: 13, color: '#374151' }}
              onClick={(e) => {
                e.stopPropagation();
                setShowDatePicker((v) => !v);
              }}
            >
              <i className="bi bi-calendar3 text-primary" />
              <span className="fw-semibold">{formatRangeLabel(dateFrom, dateTo)}</span>
              <i className="bi bi-chevron-down text-muted" style={{ fontSize: 11 }} />
            </button>

            {showDatePicker && (
              <div
                className="payment-date-panel position-absolute mt-2 bg-white border rounded-3 shadow-sm p-3"
                style={{
                  zIndex: 20,
                  borderColor: '#E5E7EB',
                  right: 0,
                  left: 'auto',
                  width: 'min(300px, calc(100vw - 2rem))',
                  maxWidth: 'calc(100vw - 2rem)',
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <label className="form-label small text-muted mb-1">From</label>
                <DateInput
                  value={dateFrom}
                  onChange={setDateFrom}
                  placeholder="From date"
                  aria-label="From date"
                  size="sm"
                  className="mb-2"
                />
                <label className="form-label small text-muted mb-1">To</label>
                <DateInput
                  value={dateTo}
                  onChange={setDateTo}
                  placeholder="To date"
                  aria-label="To date"
                  size="sm"
                  className="mb-3"
                />
                <div className="d-flex gap-2">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary flex-grow-1"
                    onClick={() => {
                      setDateFrom('');
                      setDateTo('');
                      setShowDatePicker(false);
                    }}
                  >
                    Clear
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-primary flex-grow-1"
                    onClick={() => setShowDatePicker(false)}
                  >
                    Apply
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Stat cards */}
        <div className="row g-3 mb-3 mb-md-4">
          {stats.map((s) => (
            <div key={s.label} className="col-6 col-lg-3">
              <div
                className="bg-white rounded-4 border h-100 p-3"
                style={{ borderColor: '#E8EDF5', boxShadow: '0 1px 3px rgba(15,23,42,0.04)' }}
              >
                <div
                  className="d-flex align-items-center justify-content-center rounded-3 mb-2"
                  style={{ width: 36, height: 36, background: s.iconBg, color: s.iconColor }}
                >
                  <i className={`bi ${s.icon}`} />
                </div>
                <p className="text-muted mb-1" style={{ fontSize: 12 }}>{s.label}</p>
                <h4 className="fw-bold mb-1 text-dark" style={{ fontSize: 'clamp(1.15rem, 2.5vw, 1.5rem)' }}>
                  {s.value}
                </h4>
                <p className="text-muted mb-0" style={{ fontSize: 11 }}>{s.footer}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Tabs + filter */}
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
          <div
            className="d-flex align-items-center gap-1 gap-md-3 flex-grow-1 payment-history-tabs"
            style={{ borderBottom: '1px solid #E5E7EB', overflowX: 'auto', overflowY: 'hidden' }}
          >
            {TABS.map((t) => {
              const active = tab === t.key;
              return (
                <button
                  key={t.key}
                  type="button"
                  className="btn btn-link text-decoration-none px-2 px-md-1 py-2 rounded-0 flex-shrink-0"
                  style={{
                    color: active ? '#2563EB' : '#6B7280',
                    fontWeight: active ? 700 : 500,
                    fontSize: 13,
                    borderBottom: active ? '2px solid #2563EB' : '2px solid transparent',
                    marginBottom: -1,
                  }}
                  onClick={() => setTab(t.key)}
                >
                  {t.label}
                </button>
              );
            })}
          </div>

          <div className="d-flex align-items-center gap-2">
            <span className="text-muted" style={{ fontSize: 13 }}>Filter by:</span>
            <div style={{ minWidth: 120 }}>
              <FilterSelect
                value={typeFilter}
                onChange={setTypeFilter}
                options={TYPE_FILTER_OPTIONS}
                placeholder="All"
                aria-label="Filter by type"
                size="sm"
                variant="bordered"
                menuAlign="end"
              />
            </div>
          </div>
        </div>

        {/* Table */}
        <div
          className="bg-white rounded-4 border overflow-hidden"
          style={{ borderColor: '#E8EDF5', boxShadow: '0 1px 3px rgba(15,23,42,0.04)' }}
        >
          {filtered.length === 0 ? (
            <div className="text-center py-5 px-3">
              <i className="bi bi-receipt fs-1 text-muted d-block mb-3" />
              <p className="text-muted mb-0">No payment history found.</p>
              <button
                type="button"
                className="btn btn-primary rounded-pill mt-3"
                onClick={() => navigate('/user/doctor_list')}
              >
                Book a consultation
              </button>
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <table className="table mb-0 align-middle payment-history-table">
                  <thead>
                    <tr style={{ background: '#F8FAFC' }}>
                      {['DATE & TIME', 'DOCTOR & BOOKING', 'TYPE', 'AMOUNT', 'STATUS', 'ACTION'].map((h) => (
                        <th
                          key={h}
                          className="border-0 text-muted text-uppercase fw-semibold px-3 py-3"
                          style={{ fontSize: 11, letterSpacing: '0.04em', whiteSpace: 'nowrap' }}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {pageItems.map((p) => {
                      const { date, time } = formatDatePart(p.created_at);
                      const isRefund = p.status === 'REFUNDED';
                      const isPaid = p.status === 'SUCCEEDED';
                      const isFailed = p.status === 'FAILED';

                      return (
                        <tr key={p.payment_id} style={{ borderTop: '1px solid #F1F5F9' }}>
                          <td className="px-3 py-3" style={{ minWidth: 130 }}>
                            <div className="d-flex align-items-start gap-2">
                              <i className="bi bi-calendar3 text-primary mt-1" style={{ fontSize: 14 }} />
                              <div>
                                <p className="fw-semibold mb-0 text-dark" style={{ fontSize: 13 }}>{date}</p>
                                <p className="text-muted mb-0" style={{ fontSize: 12 }}>{time}</p>
                              </div>
                            </div>
                          </td>

                          <td className="px-3 py-3" style={{ minWidth: 240 }}>
                            <div className="d-flex align-items-center gap-2">
                              <img
                                src={doctorAvatar(p.doctor_name, p.doctor_image)}
                                alt={doctorDisplayName(p.doctor_name)}
                                className="rounded-circle flex-shrink-0"
                                style={{ width: 40, height: 40, objectFit: 'cover' }}
                              />
                              <div className="min-w-0">
                                <p className="fw-bold mb-0 text-dark text-truncate" style={{ fontSize: 13 }}>
                                  {doctorDisplayName(p.doctor_name)}
                                </p>
                                <p className="text-muted mb-0 text-truncate" style={{ fontSize: 12 }}>
                                  {p.doctor_specialization || 'Consultation'}
                                  {' • '}
                                  {callTypeLabel(p.call_type)}
                                </p>
                                <p className="text-muted mb-0" style={{ fontSize: 11 }}>
                                  {p.appointment_id ? `Appt #${p.appointment_id}` : 'Appt —'}
                                  {' • '}
                                  Payment #{p.payment_id}
                                </p>
                              </div>
                            </div>
                          </td>

                          <td className="px-3 py-3">
                            <span
                              className="badge rounded-pill px-2 py-1 fw-semibold"
                              style={
                                isRefund
                                  ? { background: '#FFEDD5', color: '#C2410C', fontSize: 11 }
                                  : { background: '#D1FAE5', color: '#047857', fontSize: 11 }
                              }
                            >
                              {isRefund ? 'Refund' : 'Payment'}
                            </span>
                          </td>

                          <td className="px-3 py-3" style={{ minWidth: 140 }}>
                            <p
                              className="fw-bold mb-0"
                              style={{ fontSize: 14, color: isRefund ? '#EA580C' : '#111827' }}
                            >
                              {isRefund ? `-${formatMoney(p.total_amount)}` : formatMoney(p.total_amount)}
                            </p>
                            <p className="text-muted mb-0" style={{ fontSize: 11 }}>
                              {isRefund
                                ? 'Refund to Original Payment'
                                : `Fee ${formatMoney(p.consultation_fee)} + Platform ${formatMoney(p.processing_fee)}`}
                            </p>
                          </td>

                          <td className="px-3 py-3">
                            {isPaid && (
                              <span
                                className="badge rounded-pill px-2 py-1 fw-semibold d-inline-flex align-items-center gap-1"
                                style={{ background: '#D1FAE5', color: '#047857', fontSize: 11 }}
                              >
                                <i className="bi bi-check-circle-fill" style={{ fontSize: 10 }} />
                                Paid
                              </span>
                            )}
                            {isRefund && (
                              <span
                                className="badge rounded-pill px-2 py-1 fw-semibold d-inline-flex align-items-center gap-1"
                                style={{ background: '#FFEDD5', color: '#C2410C', fontSize: 11 }}
                              >
                                <i className="bi bi-arrow-counterclockwise" style={{ fontSize: 10 }} />
                                Refunded
                              </span>
                            )}
                            {isFailed && (
                              <span
                                className="badge rounded-pill px-2 py-1 fw-semibold"
                                style={{ background: '#FEE2E2', color: '#B91C1C', fontSize: 11 }}
                              >
                                Failed
                              </span>
                            )}
                            {!isPaid && !isRefund && !isFailed && (
                              <span
                                className="badge rounded-pill px-2 py-1 fw-semibold"
                                style={{ background: '#F3F4F6', color: '#4B5563', fontSize: 11 }}
                              >
                                {p.status}
                              </span>
                            )}
                          </td>

                          <td className="px-3 py-3">
                            <div className="d-flex align-items-center gap-2 position-relative">
                              <button
                                type="button"
                                className="btn btn-sm rounded-3 fw-semibold"
                                style={{
                                  border: '1px solid #BFDBFE',
                                  color: '#2563EB',
                                  background: '#EFF6FF',
                                  fontSize: 12,
                                  whiteSpace: 'nowrap',
                                }}
                                disabled={!p.appointment_id}
                                onClick={() => goToReceipt(p)}
                              >
                                View Receipt
                              </button>

                              <button
                                type="button"
                                className="btn btn-link text-muted p-0"
                                style={{ fontSize: 18, lineHeight: 1 }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOpenMenuId(openMenuId === p.payment_id ? null : p.payment_id);
                                }}
                                aria-label="More options"
                              >
                                <i className="bi bi-three-dots-vertical" />
                              </button>

                              {openMenuId === p.payment_id && (
                                <div
                                  className="position-absolute end-0 bg-white border rounded-3 shadow-sm py-1"
                                  style={{ top: '100%', zIndex: 15, minWidth: 160, borderColor: '#E5E7EB' }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <button
                                    type="button"
                                    className="dropdown-item small py-2 px-3"
                                    disabled={!p.appointment_id}
                                    onClick={() => {
                                      setOpenMenuId(null);
                                      goToReceipt(p);
                                    }}
                                  >
                                    <i className="bi bi-receipt me-2" />
                                    View booking
                                  </button>
                                  {p.transaction_id && (
                                    <button
                                      type="button"
                                      className="dropdown-item small py-2 px-3"
                                      onClick={() => {
                                        navigator.clipboard?.writeText(p.transaction_id);
                                        setOpenMenuId(null);
                                      }}
                                    >
                                      <i className="bi bi-clipboard me-2" />
                                      Copy txn ID
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div
                className="d-flex flex-wrap align-items-center justify-content-between gap-2 px-3 py-3"
                style={{ borderTop: '1px solid #F1F5F9' }}
              >
                <p className="text-muted mb-0" style={{ fontSize: 13 }}>
                  Showing {showingFrom} to {showingTo} of {filtered.length} payments
                </p>
                <div className="d-flex align-items-center gap-1">
                  <button
                    type="button"
                    className="btn btn-sm border rounded-2 px-2"
                    style={{ borderColor: '#E5E7EB' }}
                    disabled={currentPage <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    <i className="bi bi-chevron-left" />
                  </button>
                  {pageNumbers.map((n, idx) =>
                    n === '…' ? (
                      <span key={`e-${idx}`} className="px-1 text-muted" style={{ fontSize: 13 }}>…</span>
                    ) : (
                      <button
                        key={n}
                        type="button"
                        className="btn btn-sm rounded-2 px-2"
                        style={{
                          minWidth: 32,
                          background: n === currentPage ? '#2563EB' : '#fff',
                          color: n === currentPage ? '#fff' : '#374151',
                          border: n === currentPage ? 'none' : '1px solid #E5E7EB',
                          fontWeight: 600,
                          fontSize: 13,
                        }}
                        onClick={() => setPage(n)}
                      >
                        {n}
                      </button>
                    )
                  )}
                  <button
                    type="button"
                    className="btn btn-sm border rounded-2 px-2"
                    style={{ borderColor: '#E5E7EB' }}
                    disabled={currentPage >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                    <i className="bi bi-chevron-right" />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <style>{`
        .payment-history-table tbody tr:hover {
          background: #F8FAFC;
        }
        .payment-history-tabs::-webkit-scrollbar {
          display: none;
        }
        .payment-history-tabs {
          scrollbar-width: none;
        }
        @media (max-width: 767.98px) {
          .payment-history-table th:nth-child(3),
          .payment-history-table td:nth-child(3) {
            display: none;
          }
        }
      `}</style>
    </div>
  );
}

export default UserPaymentHistory;
